import { Check, Download } from '@mui/icons-material'
import { Box, Button, CircularProgress, SxProps, Theme } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import appConfig from '../../../config'
import { useCreateFileArchiveMutation } from '../../../graphql/mutations/fileArchives/__generated__/createFileArchiveMutation.generated'
import { FileType } from '../../../utils/fixtures'
import { useCase, useNotification } from '../../../utils/hooks'
import { startIframeDownload } from '../../../utils/startIframeDownload'

export interface DownloadAllButtonProps {
  files: FileType[]
  /** Styles the wrapper, which is only rendered together with the button. */
  sx?: SxProps<Theme>
}

// 'started': the browser has the download; there is no progress beyond that
// here (the browser shows it), so the button confirms it for a moment and
// blocks a duplicate click.
type ZipStatus = 'idle' | 'preparing' | 'started'

const STARTED_RESET_MS = 4000

// Error codes of the createFileArchive mutation that mean "too much at once"
const LIMIT_ERRORS = ['too_many_files', 'too_large']

// Saved uploads and email attachments carry attachmentSignedId; signed_id is
// set on files that were just direct-uploaded.
const getSignedId = (file: FileType) => file.attachmentSignedId || file.signed_id

/**
 * The one "download all" in the app: has the backend stream the files as a
 * ZIP. Renders nothing for fewer than two downloadable files — a single file
 * has its own download.
 */
const DownloadAllButton: React.FC<DownloadAllButtonProps> = ({ files, sx }) => {
  const { t } = useTranslation('common')
  const { setNotification } = useNotification()
  const { m2Case } = useCase() ?? {}
  const [createFileArchive] = useCreateFileArchiveMutation()
  const [zipStatus, setZipStatus] = useState<ZipStatus>('idle')

  useEffect(() => {
    if (zipStatus !== 'started') return undefined
    const timer = setTimeout(() => setZipStatus('idle'), STARTED_RESET_MS)
    return () => clearTimeout(timer)
  }, [zipStatus])

  const signedIds = files.map(getSignedId).filter((id): id is string => Boolean(id))
  if (signedIds.length < 2) return null

  // e.g. "ARAAR_105314_26 - Gruber - Dateien"; missing parts are left out
  const zipBaseName = [m2Case?.identifier, m2Case?.policyholder?.lastname, 'Dateien']
    .filter(Boolean)
    .join(' - ')

  const notifyFailure = (message: string) => {
    setZipStatus('idle')
    setNotification({ open: true, variant: 'error', message })
  }

  // The mutation only checks the files and returns a short-lived URL; the
  // backend streams the ZIP when the browser downloads it.
  const handleClick = async () => {
    if (zipStatus !== 'idle') return
    setZipStatus('preparing')
    try {
      const { data } = await createFileArchive({
        variables: { signedIds, filename: zipBaseName }
      })
      const { url, errors = [] } = data?.createFileArchive ?? {}
      if (!url) {
        notifyFailure(
          errors.some(code => LIMIT_ERRORS.includes(code))
            ? t('downloadAllTooLarge')
            : t('downloadAllFailed')
        )
        return
      }
      startIframeDownload(`${appConfig.apollo.baseUrl}${url}`, () =>
        notifyFailure(t('downloadAllFailed'))
      )
      setZipStatus('started')
    } catch (err) {
      console.error(err)
      notifyFailure(t('downloadAllFailed'))
    }
  }

  const icon = {
    idle: <Download />,
    preparing: <CircularProgress size={16} />,
    started: <Check />
  }[zipStatus]

  const label = {
    idle: t('downloadAllAsZip', { count: signedIds.length }),
    preparing: t('downloadAllPreparing'),
    started: t('downloadAllStarted')
  }[zipStatus]

  return (
    <Box sx={sx}>
      <Button
        size="small"
        variant="outlined"
        color={zipStatus === 'started' ? 'success' : 'primary'}
        startIcon={icon}
        onClick={handleClick}
        // Not disabled while 'started' (MUI would grey out the success color);
        // handleClick ignores the click instead.
        disabled={zipStatus === 'preparing'}
      >
        {label}
      </Button>
    </Box>
  )
}

export default DownloadAllButton
